import { 
  Dismiss20Regular,
  Checkmark20Regular,
  ChevronDown20Regular,
} from '@fluentui/react-icons';
import { useState } from 'react';

interface ExportarClientesModalProps {
  isOpen: boolean;
  onClose: () => void;
  totalClientesFiltrados?: number;
}

export function ExportarClientesModal({ 
  isOpen, 
  onClose,
  totalClientesFiltrados = 27 
}: ExportarClientesModalProps) {
  const [alcance, setAlcance] = useState<'todos' | 'filtrados'>('todos');
  const [formato, setFormato] = useState<'xlsx' | 'csv'>('xlsx');
  const [isFormatoDropdownOpen, setIsFormatoDropdownOpen] = useState(false);
  
  const [conteudo, setConteudo] = useState({
    dadosCliente: true,
    contactoPrincipal: true,
    informacaoFaturacao: true,
    tags: true,
    notasInternas: true,
    obrasCliente: true,
  });

  const handleExportar = () => {
    // Lógica de exportação aqui
    console.log('Exportar:', { alcance, conteudo, formato });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div 
          className="bg-card border border-border shadow-lg w-full max-w-[560px] max-h-[90vh] flex flex-col"
          style={{ borderRadius: 'var(--radius-card)' }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="p-8 pb-6 border-b border-border">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h2 
                  className="text-foreground"
                  style={{ 
                    fontSize: 'var(--text-large)',
                    fontWeight: 'var(--font-weight-semi-bold)',
                    lineHeight: '1.3'
                  }}
                >
                  Exportar Clientes
                </h2>
                <p 
                  className="text-secondary-text mt-2"
                  style={{ fontSize: 'var(--text-base)' }}
                >
                  Escolha o que pretende incluir na exportação.
                </p>
              </div>
              <button
                onClick={onClose}
                className="ml-4 p-2 hover:bg-accent rounded transition-colors"
                style={{ borderRadius: 'var(--radius)' }}
              >
                <Dismiss20Regular className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Body - Scrollable */}
          <div className="flex-1 overflow-y-auto p-8 space-y-8">
            {/* Secção 1: Alcance da Exportação */}
            <div className="space-y-4">
              <h3 
                className="text-foreground"
                style={{ 
                  fontSize: 'var(--text-section-title)',
                  fontWeight: 'var(--font-weight-semi-bold)'
                }}
              >
                Alcance da Exportação
              </h3>
              
              <div className="space-y-3">
                {/* Opção: Todos os clientes */}
                <label className="flex items-start gap-3 cursor-pointer group">
                  <div className="relative flex items-center justify-center mt-0.5">
                    <input
                      type="radio"
                      name="alcance"
                      checked={alcance === 'todos'}
                      onChange={() => setAlcance('todos')}
                      className="w-5 h-5 cursor-pointer appearance-none border-2 border-border rounded-full checked:border-primary checked:border-[6px] transition-all"
                      style={{ 
                        accentColor: 'var(--primary)',
                      }}
                    />
                  </div>
                  <div className="flex-1">
                    <span 
                      className="text-foreground"
                      style={{ fontSize: 'var(--text-base)' }}
                    >
                      Todos os clientes
                    </span>
                  </div>
                </label>

                {/* Opção: Apenas clientes filtrados */}
                <label className="flex items-start gap-3 cursor-pointer group">
                  <div className="relative flex items-center justify-center mt-0.5">
                    <input
                      type="radio"
                      name="alcance"
                      checked={alcance === 'filtrados'}
                      onChange={() => setAlcance('filtrados')}
                      className="w-5 h-5 cursor-pointer appearance-none border-2 border-border rounded-full checked:border-primary checked:border-[6px] transition-all"
                      style={{ 
                        accentColor: 'var(--primary)',
                      }}
                    />
                  </div>
                  <div className="flex-1">
                    <span 
                      className="text-foreground"
                      style={{ fontSize: 'var(--text-base)' }}
                    >
                      Apenas clientes filtrados{' '}
                      <span className="text-secondary-text">
                        ({totalClientesFiltrados} resultados)
                      </span>
                    </span>
                  </div>
                </label>
              </div>
            </div>

            {/* Secção 2: Conteúdo a Incluir */}
            <div className="space-y-4">
              <h3 
                className="text-foreground"
                style={{ 
                  fontSize: 'var(--text-section-title)',
                  fontWeight: 'var(--font-weight-semi-bold)'
                }}
              >
                Conteúdo a Incluir
              </h3>
              
              <div className="space-y-3">
                {/* Dados do Cliente */}
                <label className="flex items-start gap-3 cursor-pointer group">
                  <div className="relative flex items-center justify-center mt-0.5">
                    <input
                      type="checkbox"
                      checked={conteudo.dadosCliente}
                      onChange={(e) => setConteudo({ ...conteudo, dadosCliente: e.target.checked })}
                      className="w-5 h-5 cursor-pointer appearance-none border-2 border-border rounded checked:bg-primary checked:border-primary transition-all"
                      style={{ borderRadius: 'var(--radius)' }}
                    />
                    {conteudo.dadosCliente && (
                      <Checkmark20Regular className="w-4 h-4 text-primary-foreground absolute pointer-events-none" />
                    )}
                  </div>
                  <span 
                    className="text-foreground flex-1"
                    style={{ fontSize: 'var(--text-base)' }}
                  >
                    Dados do Cliente
                  </span>
                </label>

                {/* Contacto Principal */}
                <label className="flex items-start gap-3 cursor-pointer group">
                  <div className="relative flex items-center justify-center mt-0.5">
                    <input
                      type="checkbox"
                      checked={conteudo.contactoPrincipal}
                      onChange={(e) => setConteudo({ ...conteudo, contactoPrincipal: e.target.checked })}
                      className="w-5 h-5 cursor-pointer appearance-none border-2 border-border rounded checked:bg-primary checked:border-primary transition-all"
                      style={{ borderRadius: 'var(--radius)' }}
                    />
                    {conteudo.contactoPrincipal && (
                      <Checkmark20Regular className="w-4 h-4 text-primary-foreground absolute pointer-events-none" />
                    )}
                  </div>
                  <span 
                    className="text-foreground flex-1"
                    style={{ fontSize: 'var(--text-base)' }}
                  >
                    Contacto Principal
                  </span>
                </label>

                {/* Informação de Faturação */}
                <label className="flex items-start gap-3 cursor-pointer group">
                  <div className="relative flex items-center justify-center mt-0.5">
                    <input
                      type="checkbox"
                      checked={conteudo.informacaoFaturacao}
                      onChange={(e) => setConteudo({ ...conteudo, informacaoFaturacao: e.target.checked })}
                      className="w-5 h-5 cursor-pointer appearance-none border-2 border-border rounded checked:bg-primary checked:border-primary transition-all"
                      style={{ borderRadius: 'var(--radius)' }}
                    />
                    {conteudo.informacaoFaturacao && (
                      <Checkmark20Regular className="w-4 h-4 text-primary-foreground absolute pointer-events-none" />
                    )}
                  </div>
                  <span 
                    className="text-foreground flex-1"
                    style={{ fontSize: 'var(--text-base)' }}
                  >
                    Informação de Faturação
                  </span>
                </label>

                {/* Tags */}
                <label className="flex items-start gap-3 cursor-pointer group">
                  <div className="relative flex items-center justify-center mt-0.5">
                    <input
                      type="checkbox"
                      checked={conteudo.tags}
                      onChange={(e) => setConteudo({ ...conteudo, tags: e.target.checked })}
                      className="w-5 h-5 cursor-pointer appearance-none border-2 border-border rounded checked:bg-primary checked:border-primary transition-all"
                      style={{ borderRadius: 'var(--radius)' }}
                    />
                    {conteudo.tags && (
                      <Checkmark20Regular className="w-4 h-4 text-primary-foreground absolute pointer-events-none" />
                    )}
                  </div>
                  <span 
                    className="text-foreground flex-1"
                    style={{ fontSize: 'var(--text-base)' }}
                  >
                    Tags
                  </span>
                </label>

                {/* Notas Internas */}
                <label className="flex items-start gap-3 cursor-pointer group">
                  <div className="relative flex items-center justify-center mt-0.5">
                    <input
                      type="checkbox"
                      checked={conteudo.notasInternas}
                      onChange={(e) => setConteudo({ ...conteudo, notasInternas: e.target.checked })}
                      className="w-5 h-5 cursor-pointer appearance-none border-2 border-border rounded checked:bg-primary checked:border-primary transition-all"
                      style={{ borderRadius: 'var(--radius)' }}
                    />
                    {conteudo.notasInternas && (
                      <Checkmark20Regular className="w-4 h-4 text-primary-foreground absolute pointer-events-none" />
                    )}
                  </div>
                  <span 
                    className="text-foreground flex-1"
                    style={{ fontSize: 'var(--text-base)' }}
                  >
                    Notas Internas
                  </span>
                </label>

                {/* Obras do Cliente */}
                <label className="flex items-start gap-3 cursor-pointer group">
                  <div className="relative flex items-center justify-center mt-0.5">
                    <input
                      type="checkbox"
                      checked={conteudo.obrasCliente}
                      onChange={(e) => setConteudo({ ...conteudo, obrasCliente: e.target.checked })}
                      className="w-5 h-5 cursor-pointer appearance-none border-2 border-border rounded checked:bg-primary checked:border-primary transition-all"
                      style={{ borderRadius: 'var(--radius)' }}
                    />
                    {conteudo.obrasCliente && (
                      <Checkmark20Regular className="w-4 h-4 text-primary-foreground absolute pointer-events-none" />
                    )}
                  </div>
                  <span 
                    className="text-foreground flex-1"
                    style={{ fontSize: 'var(--text-base)' }}
                  >
                    Obras do Cliente
                  </span>
                </label>
              </div>
            </div>

            {/* Secção 3: Formato do Ficheiro */}
            <div className="space-y-4">
              <h3 
                className="text-foreground"
                style={{ 
                  fontSize: 'var(--text-section-title)',
                  fontWeight: 'var(--font-weight-semi-bold)'
                }}
              >
                Formato do Ficheiro
              </h3>

              <div className="space-y-2">
                <label 
                  className="text-foreground block"
                  style={{ 
                    fontSize: 'var(--text-base)',
                    fontWeight: 'var(--font-weight-semi-bold)'
                  }}
                >
                  Formato
                </label>

                <div className="relative">
                  <button
                    onClick={() => setIsFormatoDropdownOpen(!isFormatoDropdownOpen)}
                    className="w-full px-4 py-2.5 bg-background border border-border rounded text-left flex items-center justify-between hover:border-primary/50 transition-colors min-h-[44px]"
                    style={{ borderRadius: 'var(--radius-button)' }}
                  >
                    <span style={{ fontSize: 'var(--text-base)' }}>
                      {formato === 'xlsx' ? 'Excel (.xlsx)' : 'CSV (.csv)'}
                    </span>
                    <ChevronDown20Regular className="w-5 h-5 text-secondary-text" />
                  </button>

                  {/* Dropdown Menu */}
                  {isFormatoDropdownOpen && (
                    <>
                      {/* Backdrop */}
                      <div 
                        className="fixed inset-0 z-[60]" 
                        onClick={() => setIsFormatoDropdownOpen(false)}
                      />
                      
                      {/* Dropdown Content - Opens upward */}
                      <div 
                        className="absolute left-0 right-0 bottom-full mb-2 bg-card border border-border shadow-lg z-[70]"
                        style={{ borderRadius: 'var(--radius-card)' }}
                      >
                        <div className="py-2">
                          <button
                            onClick={() => {
                              setFormato('xlsx');
                              setIsFormatoDropdownOpen(false);
                            }}
                            className={`w-full px-4 py-2.5 text-left hover:bg-accent transition-colors ${
                              formato === 'xlsx' ? 'bg-accent' : ''
                            }`}
                            style={{ fontSize: 'var(--text-base)' }}
                          >
                            Excel (.xlsx)
                          </button>
                          <button
                            onClick={() => {
                              setFormato('csv');
                              setIsFormatoDropdownOpen(false);
                            }}
                            className={`w-full px-4 py-2.5 text-left hover:bg-accent transition-colors ${
                              formato === 'csv' ? 'bg-accent' : ''
                            }`}
                            style={{ fontSize: 'var(--text-base)' }}
                          >
                            CSV (.csv)
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Footer - Sticky */}
          <div className="p-8 pt-6 border-t border-border bg-card">
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 bg-secondary hover:bg-accent border border-border rounded transition-colors min-h-[44px]"
                style={{ 
                  borderRadius: 'var(--radius-button)',
                  fontSize: 'var(--text-base)'
                }}
              >
                Cancelar
              </button>
              <button
                onClick={handleExportar}
                className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded transition-colors min-h-[44px]"
                style={{ 
                  borderRadius: 'var(--radius-button)',
                  fontSize: 'var(--text-base)'
                }}
              >
                Exportar
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}